import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import {
  isValidDammCode,
  normalizeShareCodeDigits,
  QUICK_SHARE_CODE_DIGITS,
} from '../crypto/damm';
import {
  buildGuestUrlFromPackage,
  generateEphemeralKeyPair,
  openQuickShareFragmentPackage,
} from '../crypto/quickSharePackage';
import { claimQuickShareCode, connectQuickShareGuest } from '../ws/quickShare';

function panel(html: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'center-panel quick-share-page';
  el.innerHTML = html;
  applyI18n(el);
  return el;
}

export function QuickSharePage(): HTMLElement {
  const root = panel(`
    <h1 data-i18n="quickShare.guest.title"></h1>
    <p class="muted" data-i18n="quickShare.guest.subtitle"></p>
    <form class="form-grid" id="quick-share-form">
      <div class="quick-share-code-input" role="group" aria-label="Share code">
        <input type="text" inputmode="numeric" autocomplete="one-time-code" maxlength="4"
          class="quick-share-code-input__group" name="group1" aria-label="1" />
        <span class="quick-share-code-input__sep" aria-hidden="true">-</span>
        <input type="text" inputmode="numeric" autocomplete="one-time-code" maxlength="4"
          class="quick-share-code-input__group" name="group2" aria-label="2" />
      </div>
      <p class="muted" data-i18n="quickShare.guest.formatHint"></p>
      <p class="error-text" id="quick-share-error" hidden></p>
      <p class="muted" id="quick-share-status" hidden></p>
      <button type="submit" class="button primary" data-i18n="quickShare.guest.submit"></button>
    </form>
    <p class="muted"><a href="/guest/" data-i18n="quickShare.guest.accountLoginLink"></a></p>
  `);

  const form = root.querySelector('#quick-share-form') as HTMLFormElement;
  const group1 = form.querySelector('input[name="group1"]') as HTMLInputElement;
  const group2 = form.querySelector('input[name="group2"]') as HTMLInputElement;
  const errorEl = root.querySelector('#quick-share-error') as HTMLElement;
  const statusEl = root.querySelector('#quick-share-status') as HTMLElement;
  const submit = form.querySelector('button[type="submit"]') as HTMLButtonElement;
  let guestSocketClose: (() => void) | null = null;

  const showError = (key: string) => {
    errorEl.hidden = false;
    statusEl.hidden = true;
    errorEl.textContent = t(key);
  };

  const showStatus = (key: string) => {
    statusEl.hidden = false;
    statusEl.setAttribute('data-i18n', key);
    applyI18n(statusEl);
  };

  const readDigits = (): string =>
    normalizeShareCodeDigits(`${group1.value}${group2.value}`);

  const applyDigits = (digits: string) => {
    const normalized = normalizeShareCodeDigits(digits).slice(0, QUICK_SHARE_CODE_DIGITS);
    group1.value = normalized.slice(0, 4);
    group2.value = normalized.slice(4, 8);
  };

  let submitting = false;

  const submitCode = (): void => {
    if (submitting || submit.disabled) {
      return;
    }

    errorEl.hidden = true;
    const digits = readDigits();
    if (digits.length !== QUICK_SHARE_CODE_DIGITS) {
      showError('quickShare.guest.errorIncomplete');
      return;
    }
    if (!isValidDammCode(digits)) {
      showError('quickShare.guest.errorInvalid');
      return;
    }

    submitting = true;
    submit.disabled = true;
    showStatus('quickShare.guest.statusConnecting');
    void (async () => {
      try {
        const { privateKey, publicKey } = generateEphemeralKeyPair();
        const claimed = await claimQuickShareCode(digits, publicKey);
        showStatus('quickShare.guest.statusWaiting');

        guestSocketClose?.();
        guestSocketClose = connectQuickShareGuest({
          sessionId: claimed.sessionId,
          guestToken: claimed.guestToken,
          onWaiting: () => showStatus('quickShare.guest.statusWaiting'),
          onCancelled: () => {
            showError('quickShare.guest.errorCancelled');
            submitting = false;
            submit.disabled = false;
          },
          onPackageReady: (payload) => {
            void (async () => {
              try {
                showStatus('quickShare.guest.statusOpening');
                const fragment = await openQuickShareFragmentPackage({
                  sessionId: payload.sessionId,
                  guestPrivateKey: privateKey,
                  wrappedPackageKey: payload.wrappedPackageKey,
                  ciphertext: payload.ciphertext,
                });
                guestSocketClose?.();
                window.location.assign(buildGuestUrlFromPackage(payload.guestPath, fragment));
              } catch (cause) {
                console.error('quick share unwrap failed', cause);
                showError('quickShare.guest.errorUnwrap');
                submitting = false;
                submit.disabled = false;
              }
            })();
          },
        }).close;
      } catch (cause) {
        console.error('quick share claim failed', cause);
        const code = cause instanceof Error ? cause.message : 'claim_failed';
        if (code === 'invalid_code') showError('quickShare.guest.errorInvalid');
        else if (code === 'code_not_found' || code === 'expired') showError('quickShare.guest.errorNotFound');
        else if (code === 'already_claimed') showError('quickShare.guest.errorClaimed');
        else showError('quickShare.guest.errorGeneric');
        submitting = false;
        submit.disabled = false;
      }
    })();
  };

  const maybeAutoSubmit = (): void => {
    const digits = readDigits();
    if (digits.length !== QUICK_SHARE_CODE_DIGITS) {
      return;
    }
    if (!isValidDammCode(digits)) {
      showError('quickShare.guest.errorInvalid');
      return;
    }
    errorEl.hidden = true;
    submitCode();
  };

  const onGroupInput = (which: 1 | 2) => {
    const input = which === 1 ? group1 : group2;
    const only = normalizeShareCodeDigits(input.value).slice(0, 4);
    input.value = only;
    if (which === 1 && only.length === 4) {
      group2.focus();
    }
    if (readDigits().length < QUICK_SHARE_CODE_DIGITS) {
      errorEl.hidden = true;
    }
    maybeAutoSubmit();
  };

  group1.addEventListener('input', () => onGroupInput(1));
  group2.addEventListener('input', () => onGroupInput(2));

  const handlePaste = (event: ClipboardEvent) => {
    const text = event.clipboardData?.getData('text') ?? '';
    const digits = normalizeShareCodeDigits(text);
    if (digits.length >= 4) {
      event.preventDefault();
      applyDigits(digits);
      if (digits.length > 4) {
        group2.focus();
      }
      maybeAutoSubmit();
    }
  };
  group1.addEventListener('paste', handlePaste);
  group2.addEventListener('paste', handlePaste);

  group2.addEventListener('keydown', (event) => {
    if (event.key === 'Backspace' && group2.value.length === 0) {
      group1.focus();
    }
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submitCode();
  });

  bindLocaleRefresh(() => applyI18n(root));
  queueMicrotask(() => group1.focus());
  return root;
}
